import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiPath from '../../utils/apiPath';

// ── Fetch All (paginated + filtered) ───────────────────────────────────────
export const fetchDispatches = createAsyncThunk(
  'dispatch/fetchAll',
  async (
    { page = 1, limit = 10, status = '', customer = '', warehouse_id = '', item_code = '', date_range = '' } = {},
    { rejectWithValue }
  ) => {
    try {
      let url = `/api/v1.0/dispatch?page=${page}&limit=${limit}`;
      if (status)       url += `&status=${encodeURIComponent(status)}`;
      if (customer)     url += `&customer=${encodeURIComponent(customer)}`;
      if (warehouse_id) url += `&warehouse_id=${encodeURIComponent(warehouse_id)}`;
      if (item_code)    url += `&item_code=${encodeURIComponent(item_code)}`;
      if (date_range)   url += `&date_range=${encodeURIComponent(date_range)}`;
      const response = await apiPath.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ── Fetch By ID ────────────────────────────────────────────────────────────
export const fetchDispatchById = createAsyncThunk(
  'dispatch/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiPath.get(`/api/v1.0/dispatch/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ── Create ─────────────────────────────────────────────────────────────────
export const createDispatch = createAsyncThunk(
  'dispatch/create',
  async (dispatchData, { rejectWithValue }) => {
    try {
      const response = await apiPath.post('/api/v1.0/dispatch/create', dispatchData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ── Update ─────────────────────────────────────────────────────────────────
export const updateDispatch = createAsyncThunk(
  'dispatch/update',
  async ({ id, dispatchData }, { rejectWithValue }) => {
    try {
      const response = await apiPath.put(`/api/v1.0/dispatch/${id}`, dispatchData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ── Delete ─────────────────────────────────────────────────────────────────
export const deleteDispatch = createAsyncThunk(
  'dispatch/delete',
  async (id, { rejectWithValue }) => {
    try {
      await apiPath.delete(`/api/v1.0/dispatch/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ── Slice ──────────────────────────────────────────────────────────────────
const dispatchSlice = createSlice({
  name: 'dispatch',
  initialState: {
    dispatches:        [],
    currentDispatch:    null,
    loading:            false,
    error:              null,
    totalRecord:        0,
    totalPage:          0,
    currentPage:        1,
    perPage:            10,
  },
  reducers: {
    clearDispatchError:   (state) => { state.error = null; },
    setCurrentPage:        (state, action) => { state.currentPage = action.payload; },
    clearCurrentDispatch:  (state) => { state.currentDispatch = null; },
  },
  extraReducers: (builder) => {
    builder
      // ── Fetch All ──────────────────────────────────────────────────────
      .addCase(fetchDispatches.pending, (state) => {
        state.loading = true;
        state.error   = null;
      })
      .addCase(fetchDispatches.fulfilled, (state, action) => {
        state.loading     = false;
        state.dispatches  = action.payload.dispatches  || [];
        state.totalRecord = action.payload.total       || 0;
        state.totalPage   = action.payload.totalPage   || 0;
        state.currentPage = action.payload.currentPage || 1;
        state.perPage      = action.payload.perPage     || 10;
      })
      .addCase(fetchDispatches.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload || action.error.message;
      })

      // ── Fetch By ID ────────────────────────────────────────────────────
      .addCase(fetchDispatchById.pending,   (state) => { state.loading = true; })
      .addCase(fetchDispatchById.fulfilled, (state, action) => {
        state.loading         = false;
        state.currentDispatch = action.payload?.details || action.payload;
      })
      .addCase(fetchDispatchById.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload || action.error.message;
      })

      // ── Create ─────────────────────────────────────────────────────────
      .addCase(createDispatch.pending,   (state) => { state.loading = true; })
      .addCase(createDispatch.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.id) {
          state.dispatches  = [action.payload, ...state.dispatches];
          state.totalRecord = state.totalRecord + 1;
        }
      })
      .addCase(createDispatch.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload || action.error.message;
      })

      // ── Update ─────────────────────────────────────────────────────────
      .addCase(updateDispatch.pending,   (state) => { state.loading = true; })
      .addCase(updateDispatch.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        const index   = state.dispatches.findIndex((r) => r.id === updated.id);
        if (index !== -1) state.dispatches[index] = updated;
        if (state.currentDispatch?.id === updated.id) state.currentDispatch = updated;
      })
      .addCase(updateDispatch.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload || action.error.message;
      })

      // ── Delete ─────────────────────────────────────────────────────────
      .addCase(deleteDispatch.pending,   (state) => { state.loading = true; })
      .addCase(deleteDispatch.fulfilled, (state, action) => {
        state.loading     = false;
        state.dispatches  = state.dispatches.filter((r) => r.id !== action.payload);
        state.totalRecord = Math.max(0, state.totalRecord - 1);
      })
      .addCase(deleteDispatch.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload || action.error.message;
      });
  },
});

export const { clearDispatchError, setCurrentPage, clearCurrentDispatch } =
  dispatchSlice.actions;
export default dispatchSlice.reducer;